import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
} from '@/components/ui'
import {
  bulkEmpresas,
  createEmpresa,
  deleteEmpresa,
  fetchEmpresa,
  fetchEmpresas,
  updateEmpresa,
  type EmpresaMassaAcao,
  type EmpresaResumo,
  type EmpresaWritePayload,
} from './api'

function formatCnpj(cnpj?: string) {
  const d = (cnpj || '').replace(/\D/g, '')
  if (d.length !== 14) return cnpj || '—'
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
}

const EMPTY_FORM: EmpresaWritePayload = {
  nome: '',
  codigo_dominio: '',
  nome_fantasia: '',
  cnpj: '',
  email: '',
  telefone: '',
  cidade: '',
  estado: '',
  cnae: '',
  principal: false,
  ativo: true,
  oculta: false,
}

function visibilidadeLabel(e: EmpresaResumo) {
  if (e.oculta) return 'Oculta'
  if (e.principal) return 'Principal'
  if (e.ativo === false) return 'Inativa'
  return 'Ativa'
}

function visibilidadeTone(e: EmpresaResumo): 'ativo' | 'inativo' {
  if (e.oculta || e.ativo === false) return 'inativo'
  return 'ativo'
}

const ACOES_MASSA: { acao: EmpresaMassaAcao; label: string; confirm?: string }[] = [
  { acao: 'principal', label: 'Marcar principal' },
  { acao: 'nao_principal', label: 'Tirar principal' },
  { acao: 'ativar', label: 'Ativar' },
  { acao: 'inativar', label: 'Inativar' },
  { acao: 'ocultar', label: 'Ocultar' },
  { acao: 'mostrar', label: 'Mostrar' },
  { acao: 'excluir', label: 'Excluir', confirm: 'Excluir as empresas selecionadas?' },
]

export function EmpresasPage() {
  const { canEdit } = useAuth()
  const editable = canEdit('empresas')
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('visivel')
  const [page, setPage] = useState(1)
  const [msg, setMsg] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<EmpresaWritePayload>(EMPTY_FORM)
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const lista = useQuery({
    queryKey: ['rh', 'empresas', q, status, page],
    queryFn: () =>
      fetchEmpresas({
        busca: q || undefined,
        status: status || 'visivel',
        page,
      }),
  })

  const detalhe = useQuery({
    queryKey: ['rh', 'empresa', editingId],
    queryFn: () => fetchEmpresa(editingId!),
    enabled: Boolean(editingId),
  })

  useEffect(() => {
    if (detalhe.data && editingId) {
      const e = detalhe.data
      setForm({
        nome: e.nome || '',
        codigo_dominio: e.codigo_dominio || '',
        nome_fantasia: e.nome_fantasia || '',
        cnpj: e.cnpj || '',
        inscricao_estadual: e.inscricao_estadual || '',
        inscricao_municipal: e.inscricao_municipal || '',
        cnae: e.cnae || '',
        email: e.email || '',
        telefone: e.telefone || '',
        cep: e.cep || '',
        logradouro: e.logradouro || '',
        numero: e.numero || '',
        complemento: e.complemento || '',
        bairro: e.bairro || '',
        cidade: e.cidade || '',
        estado: e.estado || '',
        responsavel_legal: e.responsavel_legal || '',
        data_inicio: e.data_inicio || null,
        principal: Boolean(e.principal),
        ativo: e.ativo !== false,
        oculta: Boolean(e.oculta),
      })
      setShowForm(true)
    }
  }, [detalhe.data, editingId])

  // Limpa seleção ao mudar filtro/página
  useEffect(() => {
    setSelected(new Set())
  }, [q, status, page])

  const dados = lista.data?.dados || []
  const meta = lista.data?.meta
  const total = meta?.total ?? dados.length
  const pages = meta?.ultima_pagina ?? 1
  const idsPagina = useMemo(() => dados.map((e) => e.id), [dados])
  const todosSelecionados =
    idsPagina.length > 0 && idsPagina.every((id) => selected.has(id))
  const algunsSelecionados = idsPagina.some((id) => selected.has(id))

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload: EmpresaWritePayload = {
        ...form,
        nome: form.nome.trim(),
        cnpj: (form.cnpj || '').replace(/\D/g, '').slice(0, 14),
        estado: (form.estado || '').toUpperCase().slice(0, 2),
      }
      if (!payload.nome) throw new Error('Informe o nome.')
      if (editingId) return updateEmpresa(editingId, payload)
      return createEmpresa(payload)
    },
    onSuccess: async () => {
      setMsg(editingId ? 'Empresa atualizada.' : 'Empresa criada.')
      setShowForm(false)
      setEditingId(null)
      setForm(EMPTY_FORM)
      await qc.invalidateQueries({ queryKey: ['rh', 'empresas'] })
    },
    onError: (err) => {
      setMsg(err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Falha ao salvar.')
    },
  })

  const delMut = useMutation({
    mutationFn: (id: string) => deleteEmpresa(id),
    onSuccess: async () => {
      setMsg('Empresa removida.')
      setSelected((prev) => {
        const n = new Set(prev)
        // id removido já não estará na lista
        return n
      })
      await qc.invalidateQueries({ queryKey: ['rh', 'empresas'] })
    },
    onError: (err) => {
      setMsg(err instanceof ApiError ? err.message : 'Falha ao excluir.')
    },
  })

  const massaMut = useMutation({
    mutationFn: ({ ids, acao }: { ids: string[]; acao: EmpresaMassaAcao }) =>
      bulkEmpresas(ids, acao),
    onSuccess: async (res) => {
      setMsg(res.mensagem || `${res.afetados ?? 0} empresa(s) atualizada(s).`)
      setSelected(new Set())
      await qc.invalidateQueries({ queryKey: ['rh', 'empresas'] })
    },
    onError: (err) => {
      setMsg(err instanceof ApiError ? err.message : 'Falha na ação em massa.')
    },
  })

  function abrirNovo() {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setShowForm(true)
    setMsg('')
  }

  function abrirEditar(id: string) {
    setEditingId(id)
    setMsg('')
  }

  function setField<K extends keyof EmpresaWritePayload>(key: K, value: EmpresaWritePayload[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleTodosPagina() {
    setSelected((prev) => {
      const next = new Set(prev)
      if (todosSelecionados) {
        idsPagina.forEach((id) => next.delete(id))
      } else {
        idsPagina.forEach((id) => next.add(id))
      }
      return next
    })
  }

  function executarMassa(acao: EmpresaMassaAcao, confirmMsg?: string) {
    const ids = [...selected]
    if (!ids.length) {
      setMsg('Selecione ao menos uma empresa.')
      return
    }
    if (confirmMsg && !confirm(`${confirmMsg} (${ids.length})`)) return
    massaMut.mutate({ ids, acao })
  }

  return (
    <section className="hi-page">
      <PageHeader
        title="Empresas"
        subtitle="Principal primeiro, ativas em seguida; ocultas não aparecem na lista padrão. Selecione várias para CRUD em massa."
        actions={
          editable ? (
            <>
              <Link className="btn btn-secondary" to="/app/empresas/importar">
                Importar
              </Link>
              <button type="button" className="btn btn-primary" onClick={abrirNovo}>
                Nova empresa
              </button>
            </>
          ) : null
        }
      />

      {msg ? <Alert tone="info">{msg}</Alert> : null}

      <KpiGrid>
        <Kpi label="Listadas" value={String(total)} />
        <Kpi label="Selecionadas" value={String(selected.size)} />
      </KpiGrid>

      <Section title="Filtros">
        <form
          className="hi-filters"
          onSubmit={(e) => {
            e.preventDefault()
            setPage(1)
            setQ(search.trim())
          }}
        >
          <input
            type="search"
            placeholder="Nome, CNPJ ou código Domínio"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar empresas"
          />
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value)
              setPage(1)
            }}
            aria-label="Visibilidade"
          >
            <option value="visivel">Visíveis (padrão)</option>
            <option value="principal">Principais</option>
            <option value="ativo">Ativas</option>
            <option value="inativo">Inativas</option>
            <option value="oculta">Ocultas</option>
            <option value="todas">Todas</option>
          </select>
          <button type="submit" className="btn btn-secondary">
            Filtrar
          </button>
        </form>
      </Section>

      {editable && selected.size > 0 ? (
        <Section title={`Ações em massa (${selected.size})`}>
          <div className="hi-filters hi-empresa-massa">
            {ACOES_MASSA.map((a) => (
              <button
                key={a.acao}
                type="button"
                className="btn btn-secondary"
                disabled={massaMut.isPending}
                onClick={() => executarMassa(a.acao, a.confirm)}
              >
                {a.label}
              </button>
            ))}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setSelected(new Set())}
            >
              Limpar seleção
            </button>
          </div>
        </Section>
      ) : null}

      {showForm && editable ? (
        <Section title={editingId ? 'Editar empresa' : 'Nova empresa'}>
          <form
            className="hi-filters hi-empresa-form"
            onSubmit={(e) => {
              e.preventDefault()
              saveMut.mutate()
            }}
          >
            <input
              required
              placeholder="Razão social / nome *"
              value={form.nome}
              onChange={(e) => setField('nome', e.target.value)}
              aria-label="Nome"
            />
            <input
              placeholder="Nome fantasia"
              value={form.nome_fantasia || ''}
              onChange={(e) => setField('nome_fantasia', e.target.value)}
              aria-label="Nome fantasia"
            />
            <input
              placeholder="Código Domínio (codi_emp)"
              value={form.codigo_dominio || ''}
              onChange={(e) => setField('codigo_dominio', e.target.value)}
              aria-label="Código Domínio"
            />
            <input
              placeholder="CNPJ"
              value={form.cnpj || ''}
              onChange={(e) => setField('cnpj', e.target.value)}
              aria-label="CNPJ"
            />
            <input
              placeholder="E-mail"
              value={form.email || ''}
              onChange={(e) => setField('email', e.target.value)}
              aria-label="E-mail"
            />
            <input
              placeholder="Telefone"
              value={form.telefone || ''}
              onChange={(e) => setField('telefone', e.target.value)}
              aria-label="Telefone"
            />
            <input
              placeholder="Cidade"
              value={form.cidade || ''}
              onChange={(e) => setField('cidade', e.target.value)}
              aria-label="Cidade"
            />
            <input
              placeholder="UF"
              maxLength={2}
              value={form.estado || ''}
              onChange={(e) => setField('estado', e.target.value)}
              aria-label="UF"
            />
            <input
              placeholder="CNAE"
              value={form.cnae || ''}
              onChange={(e) => setField('cnae', e.target.value)}
              aria-label="CNAE"
            />
            <label className="hi-check">
              <input
                type="checkbox"
                checked={Boolean(form.principal)}
                onChange={(e) => setField('principal', e.target.checked)}
              />
              Principal
            </label>
            <label className="hi-check">
              <input
                type="checkbox"
                checked={form.ativo !== false}
                onChange={(e) => setField('ativo', e.target.checked)}
              />
              Ativa
            </label>
            <label className="hi-check">
              <input
                type="checkbox"
                checked={Boolean(form.oculta)}
                onChange={(e) => setField('oculta', e.target.checked)}
              />
              Oculta
            </label>
            <button type="submit" className="btn btn-primary" disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Salvando…' : 'Salvar'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowForm(false)
                setEditingId(null)
                setForm(EMPTY_FORM)
              }}
            >
              Cancelar
            </button>
          </form>
          {editingId ? (
            <Muted>
              Edição local protege na importação Domínio (mostra divergência e pede confirmação).
            </Muted>
          ) : null}
        </Section>
      ) : null}

      {lista.isError ? <Alert tone="danger">Falha ao carregar empresas.</Alert> : null}

      <Section title="Lista" flush>
        {!lista.isLoading && !dados.length ? <Empty>Nenhuma empresa encontrada.</Empty> : null}
        {dados.length > 0 ? (
          <DataTable>
            <thead>
              <tr>
                {editable ? (
                  <th>
                    <input
                      type="checkbox"
                      checked={todosSelecionados}
                      ref={(el) => {
                        if (el) el.indeterminate = !todosSelecionados && algunsSelecionados
                      }}
                      onChange={toggleTodosPagina}
                      aria-label="Selecionar todas nesta página"
                    />
                  </th>
                ) : null}
                <th>Cód.</th>
                <th>Nome</th>
                <th>CNPJ</th>
                <th>UF</th>
                <th>HC</th>
                <th>Visibilidade</th>
                {editable ? <th>Ações</th> : null}
              </tr>
            </thead>
            <tbody>
              {dados.map((e) => (
                <tr key={e.id} className={selected.has(e.id) ? 'hi-row-selected' : undefined}>
                  {editable ? (
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(e.id)}
                        onChange={() => toggleOne(e.id)}
                        aria-label={`Selecionar ${e.nome}`}
                      />
                    </td>
                  ) : null}
                  <td>{e.codigo_dominio || '—'}</td>
                  <td>
                    <strong>{e.nome}</strong>
                    {e.nome_fantasia ? (
                      <>
                        <br />
                        <Muted>{e.nome_fantasia}</Muted>
                      </>
                    ) : null}
                    {e.editado_manual ? (
                      <>
                        <br />
                        <Muted>Editada localmente</Muted>
                      </>
                    ) : null}
                  </td>
                  <td>{formatCnpj(e.cnpj)}</td>
                  <td>{e.estado || '—'}</td>
                  <td>{e.headcount ?? 0}</td>
                  <td>
                    <StatusBadge tone={visibilidadeTone(e)}>{visibilidadeLabel(e)}</StatusBadge>
                  </td>
                  {editable ? (
                    <td>
                      <div className="hi-actions">
                        <button type="button" className="btn btn-secondary" onClick={() => abrirEditar(e.id)}>
                          Editar
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          disabled={delMut.isPending}
                          onClick={() => {
                            if (confirm(`Excluir “${e.nome}”?`)) delMut.mutate(e.id)
                          }}
                        >
                          Excluir
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : null}
        {pages > 1 ? (
          <div className="hi-pager">
            <button
              type="button"
              className="btn btn-secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </button>
            <Muted>
              Página {page} de {pages}
            </Muted>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={page >= pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Próxima
            </button>
          </div>
        ) : null}
      </Section>
    </section>
  )
}
